/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { NetworkStatus, useApolloClient, useQuery } from '@apollo/client'
import {
  Apps,
  CloudUpload,
  ImageSearch,
  PhotoSizeSelectSmall,
  Stop,
} from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useSearchParams } from 'react-router-dom'

import {
  LABELING_EXPLORER,
  VISUAL_SEARCH,
  UltraCompanyChoices,
  UltraLabelingExplorerMode,
  UltraLabelingExplorerSort,
  type UltraLabelingExplorerQuery,
  type UltraLabelingExplorerQueryVariables,
  type UltraVisualSearchInput,
  type UltraVisualSearchQuery,
  type UltraVisualSearchQueryVariables,
} from '@dai/graphql'

import type { ClassificationBoundingBox } from './classificationTypes'
import FullImageDialog from './FullImageDialog'
import { CropSummaryCard, ImageSummaryCard } from './explorerCards'
import { useVisibleProductMap } from './explorerHooks'
import {
  type LabelingExplorerBox,
  type LabelingExplorerImage,
  type LabelingExplorerItem,
} from './explorerUtils'
import {
  DEFAULT_CLOSE_CONFIDENCE_THRESHOLD,
  DEFAULT_CROP_TILE_SIZE,
  CROP_TILE_SIZE_STEP,
  MAX_CROP_TILE_SIZE,
  MIN_CROP_TILE_SIZE,
  buildExplorerInput,
  formatUpcFilterValue,
  parseUpcs,
  normalizeCropTileSize,
  queryKeys,
  readCropTileSize,
  readFilters,
  readMode,
  readSort,
  updateSearchParam,
  updateSearchParams,
  type FilterState,
  type VisualSearchState,
} from './explorerUrlParams'
import ExplorerFilterSidebar from './ExplorerFilterSidebar'
import ImageDetailDialog from './ImageDetailDialog'
import ProductModelExclusionControl from './ProductModelExclusionControl'
import VisualSearchPanel from './VisualSearchPanel'

const LabelingExplorer = () => {
  const apolloClient = useApolloClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const mode = React.useMemo(() => readMode(searchParams), [searchParams])
  const sort = React.useMemo(() => readSort(searchParams), [searchParams])
  const filters = React.useMemo(() => readFilters(searchParams), [searchParams])
  const filteredUpcs = React.useMemo(() => parseUpcs(filters.upcs), [filters])
  const cropTileSize = React.useMemo(
    () => readCropTileSize(searchParams),
    [searchParams],
  )
  const [draftCropTileSize, setDraftCropTileSize] = React.useState(cropTileSize)
  const draftCropTileSizeRef = React.useRef(cropTileSize)

  const [selectedImage, setSelectedImage] =
    React.useState<LabelingExplorerImage | null>(null)
  const [highlightedBoxId, setHighlightedBoxId] = React.useState<string | null>(
    null,
  )
  const [dialogOpenedFromCrop, setDialogOpenedFromCrop] = React.useState(false)
  const [fullImageDialogBox, setFullImageDialogBox] =
    React.useState<ClassificationBoundingBox | null>(null)
  const [isFetchingMore, setIsFetchingMore] = React.useState(false)

  const [visualSearch, setVisualSearch] =
    React.useState<VisualSearchState | null>(null)
  const [visualSearchSourceCustomers, setVisualSearchSourceCustomers] =
    React.useState<UltraCompanyChoices[]>([])
  const lastVisualSearchRef = React.useRef<{
    anchor: { item?: LabelingExplorerItem; imagePreview?: string }
    baseInput: Omit<UltraVisualSearchInput, 'sourceCustomers' | 'topK'>
  } | null>(null)

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const input = React.useMemo(
    () => buildExplorerInput(mode, sort, filters),
    [mode, sort, filters],
  )
  const closeConfidenceThreshold = DEFAULT_CLOSE_CONFIDENCE_THRESHOLD

  React.useEffect(() => {
    setDraftCropTileSize(cropTileSize)
    draftCropTileSizeRef.current = cropTileSize
  }, [cropTileSize])

  const { data, error, fetchMore, loading, networkStatus } = useQuery<
    UltraLabelingExplorerQuery,
    UltraLabelingExplorerQueryVariables
  >(LABELING_EXPLORER, {
    variables: {
      input,
      includeImageBoxes: mode === UltraLabelingExplorerMode.IMAGE,
    },
    context: { endPoint: 'mlUltra' },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  })

  const items = data?.labelingExplorer.items ?? []
  const pageInfo = data?.labelingExplorer.pageInfo
  const totalCount = data?.labelingExplorer.totalCount
  const productsByUpc = useVisibleProductMap(
    visualSearch ? visualSearch.items : items,
    selectedImage,
  )

  // URL param updaters
  const updateUrlParam = React.useCallback(
    (key: string, value: string | null) => {
      setSearchParams(updateSearchParam(searchParams, key, value), {
        replace: true,
      })
    },
    [searchParams, setSearchParams],
  )

  const updateUrlParams = React.useCallback(
    (entries: [string, string | null][]) => {
      setSearchParams(updateSearchParams(searchParams, entries), {
        replace: true,
      })
    },
    [searchParams, setSearchParams],
  )

  const updateRangeFilter = (
    minField: keyof FilterState,
    maxField: keyof FilterState,
    minValue: string,
    maxValue: string,
  ) => {
    updateUrlParams([
      [queryKeys[minField], minValue.trim() === '' ? null : minValue],
      [queryKeys[maxField], maxValue.trim() === '' ? null : maxValue],
    ])
  }

  const resetUrlState = () =>
    setSearchParams(new URLSearchParams(), { replace: true })

  // Visual search
  const visualSearchRequestIdRef = React.useRef(0)
  const runVisualSearch = React.useCallback(
    async (
      anchor: { item?: LabelingExplorerItem; imagePreview?: string },
      queryInput: UltraVisualSearchInput,
      sourceCustomers: UltraCompanyChoices[],
    ) => {
      const searchId = visualSearchRequestIdRef.current + 1
      visualSearchRequestIdRef.current = searchId
      const { sourceCustomers: _sc, topK: _tk, ...baseInput } = queryInput
      lastVisualSearchRef.current = { anchor, baseInput }
      setVisualSearch({
        searchId,
        anchorItem: anchor.item ?? null,
        anchorImagePreview: anchor.imagePreview ?? null,
        loading: true,
        items: [],
        error: null,
      })
      try {
        const result = await apolloClient.query<
          UltraVisualSearchQuery,
          UltraVisualSearchQueryVariables
        >({
          query: VISUAL_SEARCH,
          context: { endPoint: 'mlUltra' },
          variables: {
            input: {
              ...queryInput,
              topK: 500,
              sourceCustomers:
                sourceCustomers.length > 0 ? sourceCustomers : null,
            },
          },
          fetchPolicy: 'network-only',
        })
        setVisualSearch(prev =>
          prev?.searchId === searchId
            ? { ...prev, loading: false, items: result.data.visualSearch }
            : prev,
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Search failed'
        setVisualSearch(prev =>
          prev?.searchId === searchId
            ? { ...prev, loading: false, error: message }
            : prev,
        )
      }
    },
    [apolloClient],
  )

  const startVisualSearchByBox = React.useCallback(
    (item: LabelingExplorerItem) => {
      if (!item.box) return
      void runVisualSearch(
        { item },
        { boxId: item.box.id },
        visualSearchSourceCustomers,
      )
    },
    [runVisualSearch, visualSearchSourceCustomers],
  )

  const startVisualSearchByUpload = React.useCallback(
    (file: File) => {
      const reader = new FileReader()
      reader.onload = event => {
        const dataUrl = event.target?.result as string
        const base64 = dataUrl.split(',')[1]
        void runVisualSearch(
          { imagePreview: dataUrl },
          { imageBase64: base64 },
          visualSearchSourceCustomers,
        )
      }
      reader.readAsDataURL(file)
    },
    [runVisualSearch, visualSearchSourceCustomers],
  )

  // Re-run search when source customers filter changes
  React.useEffect(() => {
    const last = lastVisualSearchRef.current
    if (!last) return
    void runVisualSearch(
      last.anchor,
      last.baseInput,
      visualSearchSourceCustomers,
    )
  }, [runVisualSearch, visualSearchSourceCustomers])

  const clearVisualSearch = React.useCallback(() => {
    setVisualSearch(null)
  }, [])

  // Pagination
  const loadMore = React.useCallback(() => {
    if (
      isFetchingMore ||
      networkStatus === NetworkStatus.fetchMore ||
      !pageInfo?.hasNextPage ||
      !pageInfo.nextCursor
    ) {
      return
    }
    setIsFetchingMore(true)
    void fetchMore({
      variables: {
        input: buildExplorerInput(mode, sort, filters, pageInfo.nextCursor),
        includeImageBoxes: mode === UltraLabelingExplorerMode.IMAGE,
      },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous
        return {
          labelingExplorer: {
            ...fetchMoreResult.labelingExplorer,
            totalCount: previous.labelingExplorer.totalCount,
            items: [
              ...previous.labelingExplorer.items,
              ...fetchMoreResult.labelingExplorer.items,
            ],
          },
        }
      },
    }).finally(() => setIsFetchingMore(false))
  }, [fetchMore, filters, isFetchingMore, mode, networkStatus, pageInfo, sort])

  React.useEffect(() => {
    if (
      loading ||
      isFetchingMore ||
      networkStatus === NetworkStatus.fetchMore ||
      !items.length ||
      !pageInfo?.hasNextPage
    ) {
      return
    }
    const animationFrameId = window.requestAnimationFrame(() => {
      const documentElement = document.documentElement
      const body = document.body
      const scrollHeight = Math.max(
        documentElement.scrollHeight,
        body?.scrollHeight ?? 0,
      )
      if (scrollHeight <= window.innerHeight + 32) {
        loadMore()
      }
    })
    return () => window.cancelAnimationFrame(animationFrameId)
  }, [
    cropTileSize,
    isFetchingMore,
    items.length,
    loadMore,
    loading,
    mode,
    networkStatus,
    pageInfo?.hasNextPage,
  ])

  // Image detail dialog
  const openImage = React.useCallback(
    async (
      image: LabelingExplorerImage,
      highlightedBox?: LabelingExplorerBox,
    ) => {
      const openedFromCrop = Boolean(highlightedBox)
      if (highlightedBox && !image.boundingBoxes) {
        const result = await apolloClient.query<
          UltraLabelingExplorerQuery,
          UltraLabelingExplorerQueryVariables
        >({
          query: LABELING_EXPLORER,
          context: { endPoint: 'mlUltra' },
          variables: {
            input: {
              mode: UltraLabelingExplorerMode.IMAGE,
              sort: UltraLabelingExplorerSort.DATE_CREATED,
              first: 1,
              filters: { imageId: image.id },
            },
            includeImageBoxes: true,
          },
          fetchPolicy: 'network-only',
        })
        setSelectedImage(result.data.labelingExplorer.items[0]?.image ?? image)
      } else {
        setSelectedImage(image)
      }
      setHighlightedBoxId(highlightedBox?.id ?? null)
      setDialogOpenedFromCrop(openedFromCrop)
    },
    [apolloClient],
  )

  const openFullImageDialog = React.useCallback(
    (image: LabelingExplorerImage, box: LabelingExplorerBox) => {
      setFullImageDialogBox({
        id: box.id,
        sourceImageUrl: image.imageUrl,
        minX: box.minX,
        maxX: box.maxX,
        minY: box.minY,
        maxY: box.maxY,
        metaClass: box.metaClass as ClassificationBoundingBox['metaClass'],
        isHumanAdded: box.isHumanAdded,
        isHumanDeleted: box.isHumanDeleted,
        isFlagged: false,
        currentUpc: box.currentUpc,
        leasedById: null,
        leasedAt: null,
        flagged: null,
      } as ClassificationBoundingBox)
    },
    [],
  )

  const visualSearchKey = visualSearch?.searchId

  return (
    <>
      <Helmet title="Labeling Explorer" />
      <Stack spacing={2}>
        {/* Header row */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Stack sx={{ flex: 1 }}>
            <Typography variant="h4">Explorer</Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            flexWrap="wrap"
          >
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, nextMode) => {
                if (nextMode) {
                  updateUrlParam(
                    queryKeys.mode,
                    nextMode === 'IMAGE' ? null : nextMode,
                  )
                }
              }}
              size="small"
            >
              <ToggleButton value="IMAGE">
                <ImageSearch fontSize="small" sx={{ mr: 1 }} />
                Images
              </ToggleButton>
              <ToggleButton value="CROP">
                <PhotoSizeSelectSmall fontSize="small" sx={{ mr: 1 }} />
                Crops
              </ToggleButton>
            </ToggleButtonGroup>
            {mode === UltraLabelingExplorerMode.CROP && (
              <Tooltip title="Crop size">
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ width: 168 }}
                >
                  <Apps fontSize="small" sx={{ flexShrink: 0 }} />
                  <Slider
                    size="small"
                    min={MIN_CROP_TILE_SIZE}
                    max={MAX_CROP_TILE_SIZE}
                    step={CROP_TILE_SIZE_STEP}
                    value={draftCropTileSize}
                    aria-label="Crop size"
                    onChange={(_, value) => {
                      const nextValue = normalizeCropTileSize(
                        Array.isArray(value) ? value[0] : value,
                      )
                      draftCropTileSizeRef.current = nextValue
                      setDraftCropTileSize(nextValue)
                    }}
                    onChangeCommitted={() => {
                      const nextValue = draftCropTileSizeRef.current
                      updateUrlParam(
                        queryKeys.cropSize,
                        nextValue === DEFAULT_CROP_TILE_SIZE
                          ? null
                          : String(nextValue),
                      )
                    }}
                    sx={{ flex: 1, minWidth: 88 }}
                  />
                  <Stop sx={{ fontSize: 28, flexShrink: 0 }} />
                </Stack>
              </Tooltip>
            )}
            <Tooltip title="Search by uploaded image">
              <Button
                size="small"
                variant={
                  visualSearch?.anchorImagePreview ? 'contained' : 'outlined'
                }
                startIcon={<CloudUpload fontSize="small" />}
                onClick={() => fileInputRef.current?.click()}
              >
                Search by image
              </Button>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={event => {
                const file = event.target.files?.[0]
                if (file) startVisualSearchByUpload(file)
                event.target.value = ''
              }}
            />
          </Stack>
        </Stack>

        {/* Visual search panel or normal explorer */}
        {visualSearch ? (
          <VisualSearchPanel
            key={visualSearchKey ?? undefined}
            visualSearch={visualSearch}
            sourceCustomers={visualSearchSourceCustomers}
            onSourceCustomersChange={setVisualSearchSourceCustomers}
            productsByUpc={productsByUpc}
            closeConfidenceThreshold={closeConfidenceThreshold}
            cropTileSize={cropTileSize}
            onClearSearch={clearVisualSearch}
            onOpenCrop={openFullImageDialog}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <ExplorerFilterSidebar
              mode={mode}
              sort={sort}
              filters={filters}
              onSortChange={value =>
                updateUrlParam(
                  queryKeys.sort,
                  value === UltraLabelingExplorerSort.DATE_CREATED
                    ? null
                    : value,
                )
              }
              onSourceCustomerChange={value =>
                updateUrlParam(queryKeys.sourceCustomer, value || null)
              }
              onUpcsChange={upcs =>
                updateUrlParam(queryKeys.upcs, formatUpcFilterValue(upcs))
              }
              onStatusesChange={statuses =>
                updateUrlParam(
                  queryKeys.statuses,
                  statuses.length ? statuses.join(',') : null,
                )
              }
              onRangeChange={updateRangeFilter}
              onCreatedAfterChange={value =>
                updateUrlParam(queryKeys.createdAfter, value)
              }
              onCreatedBeforeChange={value =>
                updateUrlParam(queryKeys.createdBefore, value || null)
              }
              onReset={resetUrlState}
            />

            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              {error && (
                <Box sx={{ p: 2 }}>
                  <Typography color="error">{error.message}</Typography>
                </Box>
              )}

              {filteredUpcs.length === 1 && (
                <ProductModelExclusionControl
                  key={filteredUpcs[0]}
                  upc={filteredUpcs[0]}
                />
              )}

              {totalCount !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  {totalCount.toLocaleString()}{' '}
                  {mode === UltraLabelingExplorerMode.CROP ? 'crops' : 'images'}
                </Typography>
              )}

              {loading &&
              networkStatus !== NetworkStatus.fetchMore &&
              !items.length ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 2,
                  }}
                >
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rounded"
                      height={320}
                      sx={{ borderRadius: 1 }}
                    />
                  ))}
                </Box>
              ) : (
                <InfiniteScroll
                  dataLength={items.length}
                  next={loadMore}
                  hasMore={Boolean(pageInfo?.hasNextPage)}
                  loader={
                    isFetchingMore ? (
                      <Stack alignItems="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </Stack>
                    ) : null
                  }
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        mode === UltraLabelingExplorerMode.CROP
                          ? `repeat(auto-fill, minmax(min(${cropTileSize}px, 100%), ${cropTileSize}px))`
                          : {
                              xs: '1fr',
                              sm: 'repeat(2, minmax(0, 1fr))',
                              lg: 'repeat(3, minmax(0, 1fr))',
                            },
                      gap: mode === UltraLabelingExplorerMode.CROP ? 1 : 2,
                    }}
                  >
                    {items.map(item =>
                      mode === UltraLabelingExplorerMode.IMAGE ? (
                        <ImageSummaryCard
                          key={item.cursor}
                          item={item}
                          closeConfidenceThreshold={closeConfidenceThreshold}
                          onOpen={openImage}
                        />
                      ) : (
                        <CropSummaryCard
                          key={item.cursor}
                          item={item}
                          productsByUpc={productsByUpc}
                          closeConfidenceThreshold={closeConfidenceThreshold}
                          tileSize={cropTileSize}
                          onOpen={openFullImageDialog}
                          onUseAsSearch={() => startVisualSearchByBox(item)}
                        />
                      ),
                    )}
                  </Box>
                </InfiniteScroll>
              )}

              {!loading && !items.length && !error && (
                <>
                  <Divider />
                  <Typography align="center" color="text.secondary">
                    No labeling items match these filters.
                  </Typography>
                </>
              )}
            </Stack>
          </Box>
        )}
      </Stack>

      <ImageDetailDialog
        image={selectedImage}
        productsByUpc={productsByUpc}
        closeConfidenceThreshold={closeConfidenceThreshold}
        highlightedBoxId={highlightedBoxId}
        openedFromCrop={dialogOpenedFromCrop}
        onClose={() => {
          setSelectedImage(null)
          setHighlightedBoxId(null)
          setDialogOpenedFromCrop(false)
        }}
        onUseAsSearch={box => {
          setSelectedImage(null)
          setHighlightedBoxId(null)
          setDialogOpenedFromCrop(false)
          if (selectedImage) {
            startVisualSearchByBox({
              cursor: '',
              image: selectedImage,
              box,
            } as LabelingExplorerItem)
          }
        }}
      />
      <FullImageDialog
        box={fullImageDialogBox}
        open={Boolean(fullImageDialogBox)}
        onClose={() => setFullImageDialogBox(null)}
        selectedUpc={fullImageDialogBox?.currentUpc}
        showSelectedProduct={false}
      />
    </>
  )
}

export default LabelingExplorer
